/* eslint-disable unicode-bom */

import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import AdminLayout from '../components/AdminLayout';
import UpdateSuccessModal from '../components/UpdateSuccessModal';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import DeleteSuccessModal from '../components/DeleteSuccessModal';
import {
  getAboutMain, createAboutMain, updateAboutMain, deleteAboutMain,
  getAllStatistics, createStatistic, updateStatistic, deleteStatistic,
  getAllFeatures, createFeature, updateFeature, deleteFeature,
  getAllTeamMembers, createTeamMember, updateTeamMember, deleteTeamMember,
} from '../services/aboutApi';
import './About.css';

// ---------------------------------------------------------------------------
// Shared constants
// ---------------------------------------------------------------------------
var ITEMS_PER_PAGE = 8;
var MAX_IMAGE_BYTES = 3.5 * 1024 * 1024; // 3.5 MB, matches Subcategory image limit

var IMAGE_BASE_URL = (
  process.env.REACT_APP_IMAGE_URL ||
  process.env.REACT_APP_BACKEND_URL ||
  process.env.VITE_BACKEND_URL ||
  'https://study.richmindx.in'
).replace(/\/+$/, '').replace(/\/api$/, '');

var resolveImageUrl = function (url) {
  if (!url || typeof url !== 'string' || !url.trim()) return '';
  var u = url.trim();
  if (u.startsWith('blob:') || u.startsWith('data:')) return u;
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  u = u.replace(/\\/g, '/');
  var uploadsIndex = u.toLowerCase().indexOf('/uploads/');
  if (uploadsIndex >= 0) u = u.slice(uploadsIndex + 1);
  u = u.replace(/^public\//, '');
  if (u.indexOf('/') === -1 && /\.(avif|gif|jpe?g|png|svg|webp)$/i.test(u)) {
    u = 'uploads/' + u;
  }
  return IMAGE_BASE_URL + '/' + u.replace(/^\/+/, '');
};

var pickImageValue = function (value) {
  if (!value) return '';
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (value && typeof value === 'object') {
    var nested = ['url', 'src', 'path', 'secure_url', 'image', 'imageUrl', 'imagePath', 'photo', 'filename', 'fileName', 'location', 'key'];
    for (var i = 0; i < nested.length; i++) {
      var n = value[nested[i]];
      if (n && typeof n === 'string' && n.trim()) return n.trim();
    }
  }
  return '';
};

var extractImageSrc = function (obj, imageKey) {
  if (!obj || typeof obj !== 'object') return '';
  var keys = [];
  if (imageKey) keys.push(imageKey);
  var commonKeys = ['image', 'imageUrl', 'image_url', 'imageURL', 'photo', 'avatar', 'picture', 'thumbnail', 'profileImage', 'img', 'src', 'path', 'url', 'icon', 'iconUrl'];
  for (var k = 0; k < commonKeys.length; k++) {
    if (keys.indexOf(commonKeys[k]) === -1) keys.push(commonKeys[k]);
  }
  for (var i = 0; i < keys.length; i++) {
    var rawVal = obj[keys[i]];
    var picked = pickImageValue(rawVal);
    if (picked) return resolveImageUrl(picked);
  }
  var arrFields = ['images', 'photos', 'pictures', 'thumbnails'];
  for (var j = 0; j < arrFields.length; j++) {
    var arr = obj[arrFields[j]];
    if (Array.isArray(arr) && arr.length > 0) {
      var firstVal = pickImageValue(arr[0]);
      if (firstVal) return resolveImageUrl(firstVal);
    }
  }
  return '';
};

var extractImageRaw = function (obj, imageKey) {
  if (!obj || typeof obj !== 'object') return '';
  var keys = [];
  if (imageKey) keys.push(imageKey);
  var commonKeys = ['image', 'imageUrl', 'image_url', 'imageURL', 'photo', 'avatar', 'picture', 'thumbnail', 'profileImage', 'img', 'src', 'path', 'url', 'icon', 'iconUrl'];
  for (var k = 0; k < commonKeys.length; k++) {
    if (keys.indexOf(commonKeys[k]) === -1) keys.push(commonKeys[k]);
  }
  for (var i = 0; i < keys.length; i++) {
    var v = obj[keys[i]];
    var picked = pickImageValue(v);
    if (picked) return picked;
  }
  return '';
};

var isActiveStatus = function (item) {
  return String((item && item.status) || 'active').toLowerCase() === 'active';
};

var sortByOrder = function (list) {
  return list.slice().sort(function (a, b) {
    var ao = (a && a.order !== undefined && a.order !== null) ? Number(a.order) : 0;
    var bo = (b && b.order !== undefined && b.order !== null) ? Number(b.order) : 0;
    return ao - bo;
  });
};

// -- Generic list unwrapper: tries the named list key, then common wrappers --
var extractArray = function (res, listKey) {
  try {
    var d = res && res.data;
    if (!d) return [];
    var arr = [];
    if (Array.isArray(d)) arr = d;
    else if (listKey && d[listKey] && Array.isArray(d[listKey])) arr = d[listKey];
    else if (d.data && Array.isArray(d.data)) arr = d.data;
    else if (d.data && listKey && d.data[listKey] && Array.isArray(d.data[listKey])) arr = d.data[listKey];
    else if (d.result && Array.isArray(d.result)) arr = d.result;
    else if (d.data && d.data.result && Array.isArray(d.data.result)) arr = d.data.result;
    return arr.filter(function (item) { return item && item.isDeleted !== true && !item.deletedAt; });
  } catch (e) { console.error('extractArray:', e); return []; }
};

var extractRecord = function (res, key) {
  try {
    var d = res && res.data;
    if (!d) return null;
    if (d._id || d.id) return d;
    if (key && d[key]) return d[key];
    if (d.data && (d.data._id || d.data.id)) return d.data;
    if (d.data && key && d.data[key]) return d.data[key];
    return null;
  } catch (e) { console.error('extractRecord:', e); return null; }
};

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------
var EyeIcon = function () {
  return React.createElement('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
    React.createElement('path', { d: 'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z' }),
    React.createElement('circle', { cx: 12, cy: 12, r: 3 })
  );
};
var PencilIcon = function () {
  return React.createElement('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
    React.createElement('path', { d: 'M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7' }),
    React.createElement('path', { d: 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z' })
  );
};
var TrashIcon = function () {
  return React.createElement('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
    React.createElement('polyline', { points: '3 6 5 6 21 6' }),
    React.createElement('path', { d: 'M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6' }),
    React.createElement('path', { d: 'M10 11v6M14 11v6' }),
    React.createElement('path', { d: 'M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2' })
  );
};
var UploadIcon = function () {
  return React.createElement('svg', { width: 28, height: 28, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round' },
    React.createElement('rect', { x: 3, y: 3, width: 18, height: 18, rx: 3 }),
    React.createElement('circle', { cx: 8.5, cy: 8.5, r: 1.5 }),
    React.createElement('polyline', { points: '21 15 16 10 5 21' })
  );
};
var XIcon = function () {
  return React.createElement('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.5, strokeLinecap: 'round', strokeLinejoin: 'round' },
    React.createElement('line', { x1: 18, y1: 6, x2: 6, y2: 18 }),
    React.createElement('line', { x1: 6, y1: 6, x2: 18, y2: 18 })
  );
};
var SearchIcon = function () {
  return React.createElement('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
    React.createElement('circle', { cx: 11, cy: 11, r: 8 }),
    React.createElement('line', { x1: 21, y1: 21, x2: 16.65, y2: 16.65 })
  );
};

var NoImgPlaceholder = function () {
  return React.createElement('div', {
    style: { width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(62,124,140,0.08)', borderRadius: 6 }
  },
    React.createElement('svg', { width: 20, height: 20, viewBox: '0 0 24 24', fill: 'none', stroke: '#475569', strokeWidth: 1.5 },
      React.createElement('rect', { x: 3, y: 3, width: 18, height: 18, rx: 3 }),
      React.createElement('circle', { cx: 8.5, cy: 8.5, r: 1.5 }),
      React.createElement('polyline', { points: '21 15 16 10 5 21' })
    )
  );
};

var EntityImage = function (props) {
  var item = props.item; var alt = props.alt; var imageKey = props.imageKey;
  var es = useState(false); var err = es[0]; var setErr = es[1];
  var fs = useState(false); var triedFallback = fs[0]; var setTriedFallback = fs[1];
  var src = extractImageSrc(item, imageKey);

  useEffect(function () {
    setErr(false);
    setTriedFallback(false);
  }, [src]);

  if (!src || err) return React.createElement(NoImgPlaceholder, null);

  return React.createElement('img', {
    src: src,
    alt: alt || 'image',
    style: { width: '100%', height: '100%', objectFit: 'cover', display: 'block', borderRadius: 6 },
    onError: function (e) {
      if (!triedFallback && src.indexOf('/uploads/') === -1 && !src.startsWith('data:') && !src.startsWith('blob:')) {
        setTriedFallback(true);
        var parts = src.split('/');
        var fn = parts.pop();
        e.target.src = parts.join('/') + '/uploads/' + fn;
      } else {
        setErr(true);
      }
    }
  });
};

// -- Unified image-validation warning modal (type or size) ------------------
var ImageWarningModal = function (props) {
  var kind = props.kind; var onClose = props.onClose;
  var title = kind === 'size' ? 'Image Size Too Large!' : 'Invalid File Type!';
  var lead = kind === 'size'
    ? React.createElement(React.Fragment, null, 'Please upload an image of size\u00A0', React.createElement('span', { style: { color: '#F28C28' } }, '3.5\u00A0MB or below'), '.')
    : React.createElement(React.Fragment, null, 'Please upload an image in ', React.createElement('span', { style: { color: '#F28C28' } }, 'JPG or PNG format'), '.');
  var sub = kind === 'size'
    ? 'Your file exceeds the 3.5\u00A0MB limit. Compress it using TinyPNG or Squoosh and try again.'
    : 'Only JPG and PNG files are supported. Convert your image and try again.';

  return React.createElement('div', {
    onClick: function (e) { if (e.target === e.currentTarget) onClose(); },
    style: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.80)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99999999, padding: 20 }
  },
    React.createElement('div', { style: { width: '100%', maxWidth: 440, borderRadius: 20, background: '#ffffff', border: '2px solid #3E7C8C', boxShadow: '0 32px 80px rgba(62,124,140,0.16)', padding: '44px 36px 36px', textAlign: 'center' } },
      React.createElement('div', { style: { width: 76, height: 76, borderRadius: '50%', margin: '0 auto 24px', background: '#eff6ff', border: '3px solid #3E7C8C', display: 'flex', alignItems: 'center', justifyContent: 'center' } },
        React.createElement('svg', { width: 38, height: 38, viewBox: '0 0 24 24', fill: 'none', stroke: '#3E7C8C', strokeWidth: 2.2, strokeLinecap: 'round', strokeLinejoin: 'round' },
          React.createElement('path', { d: 'M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z' }),
          React.createElement('line', { x1: 12, y1: 9, x2: 12, y2: 13 }),
          React.createElement('line', { x1: 12, y1: 17, x2: 12.01, y2: 17 })
        )
      ),
      React.createElement('h3', { style: { color: '#3E7C8C', fontSize: 22, fontWeight: 800, margin: '0 0 14px', fontFamily: "'Outfit',sans-serif" } }, title),
      React.createElement('p', { style: { color: '#111827', fontSize: 16, margin: '0 0 10px', lineHeight: 1.6, fontFamily: "'Outfit',sans-serif", fontWeight: 600 } }, lead),
      React.createElement('p', { style: { color: '#475569', fontSize: 13, margin: '0 0 32px', fontFamily: "'Outfit',sans-serif" } }, sub),
      React.createElement('button', {
        onClick: onClose,
        style: { padding: '13px 52px', borderRadius: 12, border: 'none', background: 'linear-gradient(90deg, #3E7C8C, #F28C28)', color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer', fontFamily: "'Outfit',sans-serif", boxShadow: '0 8px 24px rgba(62,124,140,0.24)' }
      }, 'OK, Got it')
    )
  );
};

// -- Custom Select (matches theme, no native browser highlight) -------------
var CustomSelect = function (props) {
  var value = props.value; var onChange = props.onChange; var options = props.options;
  var os = useState(false); var open = os[0]; var setOpen = os[1];
  var ref = useRef();

  useEffect(function () {
    var handler = function (e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return function () { document.removeEventListener('mousedown', handler); };
  }, []);

  var selected = options.find(function (o) { return o.value === value; });

  return React.createElement('div', { ref: ref, style: { position: 'relative' } },
    React.createElement('button', {
      type: 'button',
      onClick: function () { setOpen(!open); },
      style: {
        width: '100%', textAlign: 'left', cursor: 'pointer', padding: '11px 14px', borderRadius: 10,
        border: open ? '1px solid #3E7C8C' : '1px solid rgba(62,124,140,0.28)', background: '#FFFFFF', color: '#102a33',
        fontFamily: "'Outfit',sans-serif", fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center'
      }
    },
      selected ? selected.label : 'Select',
      React.createElement('span', { style: { fontSize: 11, color: '#2d5f6e', marginLeft: 8 } }, '\u25BC')
    ),
    open && React.createElement('div', {
      className: 'cs-dropdown-list',
      style: { position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, background: '#ffffff', border: '1px solid rgba(62,124,140,0.22)', borderRadius: 10, overflow: 'hidden', zIndex: 50, boxShadow: '0 16px 40px rgba(15,23,42,0.16)' }
    },
      options.map(function (o) {
        var isSel = o.value === value;
        return React.createElement('div', {
          key: o.value,
          onClick: function () { onChange(o.value); setOpen(false); },
          style: { padding: '10px 14px', fontSize: 13, cursor: 'pointer', color: isSel ? '#ffffff' : '#102a33', background: isSel ? '#3E7C8C' : '#ffffff', fontFamily: "'Outfit',sans-serif" },
          onMouseEnter: function (e) { if (!isSel) e.currentTarget.style.background = '#eef8fa'; },
          onMouseLeave: function (e) { if (!isSel) e.currentTarget.style.background = '#ffffff'; }
        }, o.label);
      })
    )
  );
};

var STATUS_OPTIONS = [{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }];

// -- Pagination ---------------------------------------------------------------
var pgBtn = function (disabled, active) {
  return {
    minWidth: 34, height: 34, padding: '0 10px', borderRadius: 8,
    border: active ? '1px solid #3E7C8C' : '1px solid rgba(62,124,140,0.2)',
    background: active ? '#3E7C8C' : disabled ? 'rgba(62,124,140,0.04)' : 'rgba(62,124,140,0.08)',
    color: active ? '#ffffff' : '#2d5f6e', cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 13,
    fontWeight: active ? 700 : 500, fontFamily: "'Outfit',sans-serif", transition: 'all .15s', opacity: disabled ? 0.35 : 1
  };
};

var Pagination = function (props) {
  var cp = props.currentPage; var tp = props.totalPages; var opc = props.onPageChange; var ti = props.totalItems;
  if (tp <= 1) return null;
  var si = (cp - 1) * ITEMS_PER_PAGE + 1;
  var ei = Math.min(cp * ITEMS_PER_PAGE, ti);
  var nums = (function () {
    var pages = []; var mv = 5;
    var s = Math.max(1, cp - Math.floor(mv / 2));
    var e = Math.min(tp, s + mv - 1);
    if (e - s + 1 < mv) s = Math.max(1, e - mv + 1);
    for (var i = s; i <= e; i++) pages.push(i);
    return pages;
  })();
  var btns = [
    React.createElement('button', { key: 'f', onClick: function () { opc(1); }, disabled: cp === 1, style: pgBtn(cp === 1, false) }, '\u00AB'),
    React.createElement('button', { key: 'p', onClick: function () { opc(cp - 1); }, disabled: cp === 1, style: pgBtn(cp === 1, false) }, '\u2039'),
  ];
  nums.forEach(function (n) { btns.push(React.createElement('button', { key: n, onClick: function () { opc(n); }, style: pgBtn(false, n === cp) }, n)); });
  btns.push(React.createElement('button', { key: 'n', onClick: function () { opc(cp + 1); }, disabled: cp === tp, style: pgBtn(cp === tp, false) }, '\u203A'));
  btns.push(React.createElement('button', { key: 'l', onClick: function () { opc(tp); }, disabled: cp === tp, style: pgBtn(cp === tp, false) }, '\u00BB'));
  return React.createElement('div', { className: 'pagination-row' },
    React.createElement('span', { className: 'pagination-info' }, 'Showing ', si, '\u2013', ei, ' of ', ti, ' items'),
    React.createElement('div', { style: { display: 'flex', gap: 6, alignItems: 'center' } }, btns)
  );
};

// -- Local stat card row (mirrors StatCards look for each tab) --------------
var TabStatRow = function (props) {
  var themeClasses = [' about-stat-blue', ' about-stat-teal', ' about-stat-orange', ' about-stat-purple'];
  return React.createElement('div', { className: 'about-stat-row' },
    props.cards.map(function (c, i) {
      var clickable = typeof c.onClick === 'function';
      var themeClass = themeClasses[i % themeClasses.length];
      var defaultBadgeText = i === 0 ? 'Total' : (i === 1 ? 'Active' : (i === 2 ? 'Inactive' : 'Filter'));
      return React.createElement('div', {
        key: i,
        className: 'about-stat-card' + themeClass + (clickable ? ' about-stat-card-clickable' : '') + (c.active ? ' about-stat-card-active' : ''),
        onClick: c.onClick,
        role: clickable ? 'button' : undefined,
        tabIndex: clickable ? 0 : undefined,
        onKeyDown: function (e) {
          if (clickable && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            c.onClick();
          }
        }
      },
        React.createElement('div', { className: 'about-stat-top-row' },
          React.createElement('div', { className: 'about-stat-value' }, c.value),
          c.icon
            ? React.createElement('div', { className: 'about-stat-icon' }, c.icon)
            : React.createElement('div', { className: 'about-stat-pill' },
                c.active ? React.createElement('span', { className: 'about-stat-pill-dot' }) : null,
                defaultBadgeText
              )
        ),
        React.createElement('div', { className: 'about-stat-title' }, c.title),
        c.description && React.createElement('div', { className: 'about-stat-desc' }, c.description)
      );
    })
  );
};

// ---------------------------------------------------------------------------
// Luxury View Details Modal for About Us
// ---------------------------------------------------------------------------
var AboutViewDetailsModal = function (props) {
  var item = props.item;
  var singular = props.singular;
  var fields = props.fields || [];
  var hasImage = props.hasImage;
  var imageKey = props.imageKey || 'image';
  var imageLabel = props.imageLabel || 'Photo';
  var onClose = props.onClose;
  var onEdit = props.onEdit;

  useEffect(function () {
    if (!item) return;
    var handleKeyDown = function (e) {
      if (e.key === 'Escape' && onClose) onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return function () { window.removeEventListener('keydown', handleKeyDown); };
  }, [item, onClose]);

  if (!item) return null;

  var isActive = isActiveStatus(item);
  var imgSrc = hasImage ? extractImageSrc(item, imageKey) : '';
  var descField = fields.find(function (f) { return f.type === 'textarea' || f.key === 'description'; });
  var regularFields = fields.filter(function (f) { return f.type !== 'textarea' && f.key !== 'description'; });
  var descriptionText = descField ? (item[descField.key] || '') : '';

  var modalContent = React.createElement('div', {
    className: 'abt-view-overlay',
    onClick: function (e) { if (e.target === e.currentTarget && onClose) onClose(); }
  },
    React.createElement('div', {
      className: 'abt-view-card',
      role: 'dialog',
      'aria-modal': 'true',
      'aria-labelledby': 'abt-view-modal-title'
    },
      // Header
      React.createElement('div', { className: 'abt-view-header' },
        React.createElement('div', { className: 'abt-view-header-left' },
          React.createElement('div', { className: 'abt-view-icon-badge' },
            React.createElement(EyeIcon, null)
          ),
          React.createElement('div', null,
            React.createElement('h3', { id: 'abt-view-modal-title', className: 'abt-view-title' },
              singular === 'Feature' ? 'View Feature Details' : ('View ' + singular + ' Details')
            ),
            React.createElement('p', { className: 'abt-view-subtitle' },
              'Review configured content and live status'
            )
          )
        ),
        React.createElement('button', {
          type: 'button',
          className: 'abt-view-close-btn',
          onClick: onClose,
          'aria-label': 'Close'
        }, React.createElement(XIcon, null))
      ),

      // Scrollable Body
      React.createElement('div', { className: 'abt-view-body' },
        // Hero Highlight Banner
        React.createElement('div', { className: 'abt-view-hero-banner' },
          React.createElement('div', { className: 'abt-view-hero-info' },
            singular === 'Feature' ? React.createElement(React.Fragment, null,
              React.createElement('div', { className: 'abt-view-chip abt-view-chip-icon' },
                React.createElement('span', { className: 'abt-chip-sparkle' }, '\u2726'),
                ' ' + (item.icon || 'diamond')
              ),
              React.createElement('div', { className: 'abt-view-hero-title' }, item.title || 'Untitled Feature')
            ) : singular === 'Statistic' ? React.createElement(React.Fragment, null,
              React.createElement('div', { className: 'abt-view-hero-metric' }, item.value || '\u2014'),
              React.createElement('div', { className: 'abt-view-hero-title' }, item.title || 'Untitled Statistic')
            ) : singular === 'Team Member' ? React.createElement(React.Fragment, null,
              React.createElement('div', { className: 'abt-view-hero-title' }, item.name || 'Team Member'),
              React.createElement('div', { className: 'abt-view-chip abt-view-chip-role' }, item.designation || 'Member')
            ) : React.createElement(React.Fragment, null,
              React.createElement('div', { className: 'abt-view-hero-title' }, item.title || item.name || singular)
            )
          ),
          React.createElement('div', { className: 'abt-view-hero-status' },
            React.createElement('span', { className: 'abt-status-pill ' + (isActive ? 'active' : 'inactive') },
              React.createElement('span', { className: 'abt-status-dot' }),
              isActive ? 'ACTIVE' : 'INACTIVE'
            )
          )
        ),

        // Photo preview if applicable
        hasImage && React.createElement('div', { className: 'abt-view-photo-card' },
          imgSrc ? (
            React.createElement('div', { className: 'abt-view-photo-wrap' },
              React.createElement('img', {
                src: imgSrc,
                alt: item.name || item.title || imageLabel,
                className: 'abt-view-photo-img',
                onError: function (e) {
                  e.target.style.display = 'none';
                  if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                }
              }),
              React.createElement('div', { className: 'abt-view-photo-fallback', style: { display: 'none' } },
                React.createElement(NoImgPlaceholder, null),
                React.createElement('span', null, 'Image preview not available')
              )
            )
          ) : (
            React.createElement('div', { className: 'abt-view-photo-empty' },
              React.createElement(NoImgPlaceholder, null),
              React.createElement('span', null, 'No photo uploaded')
            )
          )
        ),

        // Structured key-value properties list
        React.createElement('div', { className: 'abt-view-props-card' },
          regularFields.map(function (f) {
            var val = item[f.key];
            var displayVal = (val !== undefined && val !== null && val !== '') ? String(val) : '\u2014';
            return React.createElement('div', { className: 'abt-view-prop-row', key: f.key },
              React.createElement('div', { className: 'abt-view-prop-label' },
                React.createElement('span', { className: 'abt-prop-bullet' }),
                f.label
              ),
              React.createElement('div', { className: 'abt-view-prop-value' },
                f.key === 'order' ? (
                  React.createElement('span', { className: 'abt-order-badge' }, '# ' + displayVal)
                ) : f.key === 'icon' ? (
                  React.createElement('span', { className: 'abt-icon-badge' }, '\u2726 ' + displayVal)
                ) : displayVal
              )
            );
          }),
          // Status row
          React.createElement('div', { className: 'abt-view-prop-row' },
            React.createElement('div', { className: 'abt-view-prop-label' },
              React.createElement('span', { className: 'abt-prop-bullet' }),
              'Status'
            ),
            React.createElement('div', { className: 'abt-view-prop-value' },
              React.createElement('span', { className: 'abt-status-tag ' + (isActive ? 'active' : 'inactive') },
                isActive ? 'Active' : 'Inactive'
              )
            )
          )
        ),

        // Dedicated Description Block
        (descriptionText || descField) && React.createElement('div', { className: 'abt-view-desc-card' },
          React.createElement('div', { className: 'abt-view-desc-hdr' },
            React.createElement('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
              React.createElement('path', { d: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' }),
              React.createElement('polyline', { points: '14 2 14 8 20 8' }),
              React.createElement('line', { x1: 16, y1: 13, x2: 8, y2: 13 }),
              React.createElement('line', { x1: 16, y1: 17, x2: 8, y2: 17 }),
              React.createElement('polyline', { points: '10 9 9 9 8 9' })
            ),
            descField ? descField.label.toUpperCase() : 'DESCRIPTION'
          ),
          React.createElement('div', { className: 'abt-view-desc-body' },
            descriptionText ? descriptionText : React.createElement('span', { className: 'abt-empty-desc' }, 'No description provided.')
          )
        )
      ),

      // Footer
      React.createElement('div', { className: 'abt-view-footer' },
        onEdit && React.createElement('button', {
          type: 'button',
          className: 'abt-view-btn-edit',
          onClick: function () { onEdit(item); }
        },
          React.createElement(PencilIcon, null),
          'Edit ' + singular
        ),
        React.createElement('button', {
          type: 'button',
          className: 'abt-view-btn-close',
          onClick: onClose
        }, 'Close')
      )
    )
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

// ---------------------------------------------------------------------------
// Generic CRUD list manager — reused for Statistics / Crafted With Purpose / Team
// ---------------------------------------------------------------------------
var ManagedTable = function (props) {
  var singular = props.singular;         // e.g. 'Statistic'
  var plural = props.plural;             // e.g. 'Statistics'
  var addLabel = props.addLabel;         // e.g. 'Add Statistic'
  var hasImage = props.hasImage;         // bool
  var imageLabel = props.imageLabel;     // e.g. 'Icon' / 'Photo'
  var imageHint = props.imageHint;       // upload hint text
  var imageKey = props.imageKey || 'image';
  var fields = props.fields;             // [{key,label,type,required,maxLength,placeholder,showInTable}]
  var listKey = props.listKey;
  var api = props.api;                   // {getAll, create, update, remove}

  var b1 = useState([]); var items = b1[0]; var setItems = b1[1];
  var b2 = useState(true); var loading = b2[0]; var setLoading = b2[1];
  var b3 = useState(false); var submitLoading = b3[0]; var setSubmitLoading = b3[1];
  var b4 = useState(1); var currentPage = b4[0]; var setCurrentPage = b4[1];
  var b5 = useState(false); var showModal = b5[0]; var setShowModal = b5[1];
  var b6 = useState(null); var editId = b6[0]; var setEditId = b6[1];
  var b7 = useState(null); var viewItem = b7[0]; var setViewItem = b7[1];
  var b8 = useState(null); var pendingDelete = b8[0]; var setPendingDelete = b8[1];
  var b9 = useState({ text: '', type: '' }); var msg = b9[0]; var setMsg = b9[1];
  var b10 = useState({}); var formValues = b10[0]; var setFormValues = b10[1];
  var b11 = useState('active'); var status = b11[0]; var setStatus = b11[1];
  var b12 = useState(null); var imageFile = b12[0]; var setImageFile = b12[1];
  var b13 = useState(''); var imagePreview = b13[0]; var setImagePreview = b13[1];
  var b14 = useState(false); var imageRemoved = b14[0]; var setImageRemoved = b14[1];
  var b15 = useState(false); var showSizeWarn = b15[0]; var setShowSizeWarn = b15[1];
  var b16 = useState(false); var showTypeWarn = b16[0]; var setShowTypeWarn = b16[1];
  var bStatusFilter = useState('all'); var statusFilter = bStatusFilter[0]; var setStatusFilter = bStatusFilter[1];
  var bSearch = useState(''); var search = bSearch[0]; var setSearch = bSearch[1];
  var bSuccess = useState({ isOpen: false, message: '', subtitle: '' }); var successModal = bSuccess[0]; var setSuccessModal = bSuccess[1];
  var bDelSuccess = useState(null); var deleteSuccess = bDelSuccess[0]; var setDeleteSuccess = bDelSuccess[1];

  var fileInputRef = useRef();
  var tableRef = useRef(null);

  var showMsg = function (text, type) {
    setMsg({ text: text, type: type });
    setTimeout(function () { setMsg({ text: '', type: '' }); }, 5000);
  };

  var resetFileInput = function () { try { if (fileInputRef.current) fileInputRef.current.value = ''; } catch (e) { /* noop */ } };

  var clearImage = function () { setImagePreview(''); setImageFile(null); setImageRemoved(true); resetFileInput(); };

  var resetForm = function () {
    var defaults = {};
    fields.forEach(function (f) { defaults[f.key] = ''; });
    setFormValues(defaults);
    setStatus('active');
    setEditId(null);
    clearImage();
    setImageRemoved(false);
  };

  var closeModal = function () { setShowModal(false); resetForm(); setMsg({ text: '', type: '' }); };

  var validateAndSetImage = function (file) {
    if (!file) return;
    if (file.type !== 'image/jpeg' && file.type !== 'image/png') { resetFileInput(); setShowTypeWarn(true); return; }
    if (file.size > MAX_IMAGE_BYTES) { resetFileInput(); setShowSizeWarn(true); return; }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setImageRemoved(false);
  };

  var handleImageChange = function (e) { validateAndSetImage(e.target.files && e.target.files[0]); };
  var handleImageDrop = function (e) {
    e.preventDefault();
    e.currentTarget.classList.remove('drag-over');
    validateAndSetImage(e.dataTransfer.files && e.dataTransfer.files[0]);
  };

  var fetchData = function () {
    setLoading(true);
    api.getAll()
      .then(function (res) { setItems(extractArray(res, listKey)); setCurrentPage(1); })
      .catch(function (err) {
        if (err && err.response && (err.response.status === 404 || err.response.status === 403)) {
          setItems([]);
          setCurrentPage(1);
          return;
        }
        showMsg((err.response && err.response.data && (err.response.data.msg || err.response.data.message)) || ('Failed to load ' + plural.toLowerCase()), 'e');
      })
      .finally(function () { setLoading(false); });
  };

  useEffect(function () { fetchData(); }, []); // eslint-disable-line

  var handleSubmit = async function () {
    for (var i = 0; i < fields.length; i++) {
      var f = fields[i];
      var raw = String(formValues[f.key] || '');
      if (f.required && !raw.trim()) {
        showMsg(f.label + ' is required', 'e');
        return;
      }
      if ((f.key === 'name' || f.key === 'designation') && raw.trim()) {
        if (!/^[a-zA-Z\s]+$/.test(raw.trim())) {
          showMsg(f.label + ' can only contain letters and spaces', 'e');
          return;
        }
      }
    }
    if (imageFile && imageFile.size > MAX_IMAGE_BYTES) {
      setImageFile(null); setImagePreview(''); resetFileInput(); setShowSizeWarn(true); return;
    }

    setSubmitLoading(true);

    var onSuccess = function () {
      if (editId) {
        if (singular === 'Team Member') {
          setSuccessModal({ isOpen: true, message: 'Team member updated successfully!', subtitle: 'Your changes have been saved.' });
        } else {
          setSuccessModal({ isOpen: true, message: singular + ' updated successfully!', subtitle: 'Your changes have been saved.' });
        }
      } else {
        showMsg(singular + ' Created! \u2705', 's');
      }
      closeModal(); fetchData(); setSubmitLoading(false);
    };
    var onFail = function (err) {
      console.error('Submit failed:', err);
      showMsg((err.response && err.response.data && (err.response.data.message || err.response.data.msg)) || 'An error occurred', 'e');
      setSubmitLoading(false);
    };

    try {
      var fd = new FormData();
      fields.forEach(function (f) {
        var rawVal = formValues[f.key];
        if (f.type === 'number') {
          var num = rawVal !== '' && rawVal !== undefined && rawVal !== null && !isNaN(Number(rawVal)) ? Number(rawVal) : 0;
          fd.append(f.key, String(num));
        } else {
          fd.append(f.key, (rawVal || '').toString().trim());
        }
      });
      fd.append('status', status);
      if (hasImage) {
        if (imageRemoved) fd.append('removeImage', 'true');
        else if (imageFile) fd.append(imageKey, imageFile);
        // otherwise: leave existing image untouched, backend keeps it
      }
      await (editId ? api.update(editId, fd) : api.create(fd));
      onSuccess();
    } catch (err) { onFail(err); }
  };

  var handleEdit = function (item) {
    setEditId(item._id || item.id);
    var vals = {};
    fields.forEach(function (f) {
      var v = item[f.key];
      vals[f.key] = (v !== undefined && v !== null) ? String(v) : '';
    });
    setFormValues(vals);
    setStatus(item.status || 'active');
    if (hasImage) {
      var existingImg = extractImageSrc(item, imageKey);
      setImagePreview(existingImg);
      setImageFile(null);
      setImageRemoved(false);
    }
    setShowModal(true);
  };

  var handleDelete = function (item) {
    var id = item._id || item.id;
    if (!id) { showMsg('Cannot delete: missing ID', 'e'); return; }
    api.remove(id)
      .then(function () {
        var subText = singular === 'Team Member'
          ? 'The team member has been deleted successfully.'
          : ('The ' + singular.toLowerCase() + ' has been deleted successfully.');
        setDeleteSuccess({ id: id, subtitle: subText });
      })
      .catch(function (err) {
        showMsg((err.response && err.response.data && (err.response.data.msg || err.response.data.message)) || 'Delete failed', 'e');
      });
  };

  var handleDeleteSuccessClose = function () {
    var id = deleteSuccess && deleteSuccess.id;
    setDeleteSuccess(null);
    if (id) {
      setItems(function (prev) { return prev.filter(function (s) { return (s._id || s.id) !== id; }); });
    }
    fetchData();
  };

  var requestDelete = function (item) { if (!item) { showMsg('Cannot delete: invalid item', 'e'); return; } setPendingDelete(item); };
  var cancelDelete = function () { setPendingDelete(null); };
  var confirmDelete = function () { if (!pendingDelete) return; var s = pendingDelete; setPendingDelete(null); handleDelete(s); };

  // -- Quick Active/Inactive toggle without opening the edit modal --
  var handleToggleStatus = function (item) {
    var id = item._id || item.id;
    if (!id) return;
    var nextStatus = isActiveStatus(item) ? 'inactive' : 'active';
    setItems(function (prev) { return prev.map(function (s) { return (s._id || s.id) === id ? Object.assign({}, s, { status: nextStatus }) : s; }); });
    var fd = new FormData();
    fields.forEach(function (f) {
      var v = item[f.key];
      fd.append(f.key, v !== undefined && v !== null ? String(v) : '');
    });
    fd.append('status', nextStatus);
    api.update(id, fd)
      .then(function () { showMsg(singular + ' marked ' + nextStatus + '! \u2705', 's'); })
      .catch(function (err) {
        showMsg((err.response && err.response.data && (err.response.data.msg || err.response.data.message)) || 'Status update failed', 'e');
        fetchData();
      });
  };

  var handleStatClick = function (targetStatus) {
    setStatusFilter(targetStatus);
    setCurrentPage(1);
    if (tableRef.current) {
      tableRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  var sortedItems = sortByOrder(items);
  var activeCount = items.filter(isActiveStatus).length;
  var inactiveCount = items.length - activeCount;
  var filteredItems = sortedItems.filter(function (item) {
    if (statusFilter === 'active' && !isActiveStatus(item)) return false;
    if (statusFilter === 'inactive' && isActiveStatus(item)) return false;
    if (search && search.trim()) {
      var q = search.trim().toLowerCase();
      var match = false;
      fields.forEach(function (f) {
        var v = item[f.key];
        if (v !== undefined && v !== null && String(v).toLowerCase().indexOf(q) !== -1) {
          match = true;
        }
      });
      if (!match) return false;
    }
    return true;
  });
  var totalPages = Math.ceil(filteredItems.length / ITEMS_PER_PAGE);
  var pageStart = (currentPage - 1) * ITEMS_PER_PAGE;
  var pageItems = filteredItems.slice(pageStart, pageStart + ITEMS_PER_PAGE);
  var thumbStyle = { width: 44, height: 44, minWidth: 44, flexShrink: 0, borderRadius: 8, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(62,124,140,0.08)', border: '1px solid rgba(62,124,140,0.15)' };
  var tableFields = fields.filter(function (f) { return f.showInTable; });

  return React.createElement(React.Fragment, null,

    showSizeWarn && React.createElement(ImageWarningModal, { kind: 'size', onClose: function () { setShowSizeWarn(false); } }),
    showTypeWarn && React.createElement(ImageWarningModal, { kind: 'type', onClose: function () { setShowTypeWarn(false); } }),

    // -- Update / Delete Success Modal --
    successModal.isOpen && React.createElement(UpdateSuccessModal, {
      isOpen: true,
      message: successModal.message,
      subtitle: successModal.subtitle,
      onClose: function () { setSuccessModal({ isOpen: false, message: '', subtitle: '' }); }
    }),

    // -- View modal --
    viewItem && React.createElement(AboutViewDetailsModal, {
      item: viewItem,
      singular: singular,
      fields: fields,
      hasImage: hasImage,
      imageKey: imageKey,
      imageLabel: imageLabel,
      onClose: function () { setViewItem(null); },
      onEdit: function (item) { setViewItem(null); handleEdit(item); }
    }),

    // -- Add / Edit modal --
    showModal && React.createElement('div', { className: 'modal-overlay', onClick: function (e) { if (e.target.classList.contains('modal-overlay')) closeModal(); } },
      React.createElement('div', { className: 'modal-box' },
        React.createElement('div', { className: 'modal-hdr' },
          React.createElement('span', { className: 'modal-title' }, editId ? ('Edit ' + singular) : ('Add New ' + singular)),
          React.createElement('button', { className: 'modal-close', type: 'button', onClick: closeModal, 'aria-label': 'Close' }, React.createElement(XIcon, null))
        ),
        msg.text && React.createElement('div', { className: 'mbx ' + (msg.type === 'e' ? 'mbx-e' : 'mbx-s') }, msg.text),

        hasImage && React.createElement('div', { className: 'fl' },
          React.createElement('label', null, imageLabel),
          imagePreview
            ? React.createElement('div', { className: 'img-preview-wrap' },
                React.createElement('input', { type: 'file', accept: 'image/jpeg,image/png', ref: fileInputRef, style: { display: 'none' }, onChange: handleImageChange }),
                React.createElement('img', {
                  src: imagePreview,
                  alt: 'preview',
                  className: 'img-preview',
                  onError: function (e) {
                    if (imagePreview && imagePreview.indexOf('/uploads/') === -1 && !imagePreview.startsWith('blob:') && !imagePreview.startsWith('data:')) {
                      var parts = imagePreview.split('/');
                      var fn = parts.pop();
                      e.target.src = parts.join('/') + '/uploads/' + fn;
                    } else {
                      e.target.onerror = null;
                      e.target.style.display = 'none';
                      var ph = e.target.parentElement && e.target.parentElement.querySelector('.img-preview-fallback');
                      if (ph) ph.style.display = 'flex';
                    }
                  }
                }),
                React.createElement('div', { className: 'img-preview-fallback', style: { display: 'none' } },
                  React.createElement('span', null, 'Photo preview not available')
                ),
                React.createElement('div', { className: 'img-preview-actions' },
                  React.createElement('button', { className: 'img-btn', type: 'button', onClick: function (e) { e.stopPropagation(); if (fileInputRef.current) fileInputRef.current.click(); } }, 'Change Photo'),
                  React.createElement('button', { className: 'img-remove', type: 'button', title: 'Remove photo', onClick: function (e) { e.stopPropagation(); clearImage(); } }, React.createElement(XIcon, null))
                )
              )
            : React.createElement('div', { className: 'img-upload-zone', onClick: function () { if (fileInputRef.current) fileInputRef.current.click(); }, onDragOver: function (e) { e.preventDefault(); e.currentTarget.classList.add('drag-over'); }, onDragLeave: function (e) { e.currentTarget.classList.remove('drag-over'); }, onDrop: handleImageDrop },
                React.createElement('input', { type: 'file', accept: 'image/jpeg,image/png', ref: fileInputRef, style: { display: 'none' }, onChange: handleImageChange }),
                React.createElement('span', { style: { color: '#3E7C8C' } }, React.createElement(UploadIcon, null)),
                React.createElement('span', { className: 'img-upload-hint' }, React.createElement('strong', null, 'Click to upload'), ' or drag & drop', React.createElement('br', null), imageHint || 'PNG, JPG \u2014 max 3.5\u00A0MB')
              )
        ),

        fields.map(function (f) {
          var val = formValues[f.key] || '';
          if (f.type === 'textarea') {
            return React.createElement('div', { className: 'fl', key: f.key },
              React.createElement('label', null, f.label + (f.required ? ' *' : ' (Optional)'), f.maxLength && React.createElement('span', { className: 'char-count' + (val.length >= f.maxLength ? ' char-warn' : '') }, val.length + '/' + f.maxLength)),
              React.createElement('textarea', { rows: 3, placeholder: f.placeholder || '', value: val, maxLength: f.maxLength, onChange: function (e) { setFormValues(function (prev) { var next = Object.assign({}, prev); next[f.key] = e.target.value; return next; }); } })
            );
          }
          return React.createElement('div', { className: 'fl', key: f.key },
            React.createElement('label', null, f.label + (f.required ? ' *' : ' (Optional)'), f.maxLength && React.createElement('span', { className: 'char-count' + (val.length >= f.maxLength ? ' char-warn' : '') }, val.length + '/' + f.maxLength)),
            React.createElement('input', {
              type: f.type === 'number' ? 'number' : 'text',
              placeholder: f.placeholder || '',
              value: val,
              maxLength: f.type === 'number' ? undefined : f.maxLength,
              onChange: function (e) {
                var v = e.target.value;
                if (f.key === 'name' || f.key === 'designation') {
                  v = v.replace(/[^a-zA-Z\s]/g, '');
                }
                setFormValues(function (prev) { var next = Object.assign({}, prev); next[f.key] = v; return next; });
              }
            })
          );
        }),

        React.createElement('div', { className: 'fl' },
          React.createElement('label', null, 'Status'),
          React.createElement(CustomSelect, { value: status, onChange: setStatus, options: STATUS_OPTIONS })
        ),

        React.createElement('div', { className: 'fa' },
          React.createElement('button', { className: 'btn btn-g', type: 'button', onClick: closeModal }, 'Cancel'),
          React.createElement('button', { className: 'btn btn-p', type: 'button', disabled: submitLoading, style: { opacity: submitLoading ? 0.7 : 1 }, onClick: handleSubmit }, submitLoading ? 'Saving...' : (editId ? ('Update ' + singular) : ('Add ' + singular)))
        )
      )
    ),

    // -- Delete confirm --
    pendingDelete && React.createElement(DeleteConfirmModal, {
      itemName: pendingDelete.title || pendingDelete.name || pendingDelete.value,
      message: 'Are you sure you want to delete this ' + (singular === 'Team Member' ? 'team member' : singular.toLowerCase()) + '?',
      onConfirm: confirmDelete,
      onCancel: cancelDelete
    }),

    // -- Delete Success Modal --
    deleteSuccess && React.createElement(DeleteSuccessModal, {
      isOpen: true,
      message: 'Deleted Successfully',
      subtitle: deleteSuccess.subtitle || 'The item has been deleted successfully.',
      onClose: handleDeleteSuccessClose
    }),

    msg.text && !showModal && React.createElement('div', { className: 'mbx ' + (msg.type === 'e' ? 'mbx-e' : 'mbx-s') }, msg.text),

    React.createElement(TabStatRow, {
      cards: [
        { title: 'Total ' + plural, value: items.length, description: 'All records', active: statusFilter === 'all', onClick: function () { handleStatClick('all'); } },
        { title: 'Active ' + plural, value: activeCount, description: 'Visible on About page', active: statusFilter === 'active', onClick: function () { handleStatClick('active'); } },
        { title: 'Inactive ' + plural, value: inactiveCount, description: 'Hidden from users', active: statusFilter === 'inactive', onClick: function () { handleStatClick('inactive'); } }
      ]
    }),

    React.createElement('div', { className: 'wid', ref: tableRef },
      React.createElement('div', { className: 'wh' },
        React.createElement('span', { className: 'wt' }, (statusFilter === 'all' ? 'All ' : (statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1) + ' ')) + plural + ' (' + filteredItems.length + ')'),
        React.createElement('div', { className: 'wh-actions' },
          React.createElement('div', { className: 'about-search-box' },
            React.createElement('span', { className: 'about-search-icon' }, React.createElement(SearchIcon, null)),
            React.createElement('input', {
              type: 'text',
              placeholder: 'Search ' + plural.toLowerCase() + '...',
              value: search,
              onChange: function (e) { setSearch(e.target.value); setCurrentPage(1); }
            }),
            search && React.createElement('button', {
              type: 'button',
              className: 'about-search-clear',
              onClick: function () { setSearch(''); setCurrentPage(1); },
              title: 'Clear search'
            }, React.createElement(XIcon, null))
          ),
          React.createElement('button', { className: 'add-btn', type: 'button', onClick: function () { resetForm(); setShowModal(true); } },
            React.createElement('svg', { viewBox: '0 0 24 24', style: { width: 15, height: 15, fill: '#fff' } }, React.createElement('path', { d: 'M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z' })), ' ', addLabel
          )
        )
      ),
      loading
        ? React.createElement('div', { className: 'loading-spin' })
        : React.createElement(React.Fragment, null,
            React.createElement('div', { style: { overflowX: 'auto' } },
              React.createElement('table', { className: 'tbl' },
                React.createElement('thead', null,
                  React.createElement('tr', null,
                    React.createElement('th', null, 'S.No'),
                    hasImage && React.createElement('th', null, imageLabel),
                    tableFields.map(function (f) { return React.createElement('th', { key: f.key }, f.label); }),
                    React.createElement('th', null, 'Status'),
                    React.createElement('th', null, 'Actions')
                  )
                ),
                React.createElement('tbody', null,
                  pageItems.length === 0
                    ? React.createElement('tr', null, React.createElement('td', { colSpan: 3 + tableFields.length + (hasImage ? 1 : 0), style: { textAlign: 'center', color: '#94a3b8', padding: 32 } }, 'No ' + plural.toLowerCase() + ' found.'))
                    : pageItems.map(function (item, idx) {
                        var st = (item.status || 'active').toLowerCase();
                        var sid = item._id || item.id || String(idx);
                        return React.createElement('tr', { key: sid },
                          React.createElement('td', { style: { color: '#111827', fontSize: 12 } }, pageStart + idx + 1),
                          hasImage && React.createElement('td', null, React.createElement('div', { style: thumbStyle }, React.createElement(EntityImage, { item: item, imageKey: imageKey, alt: item.title || item.name }))),
                          tableFields.map(function (f) {
                            var v = item[f.key];
                            if (f.type === 'number') return React.createElement('td', { key: f.key, style: { color: '#111827', fontSize: 12 } }, v !== undefined && v !== null ? v : 0);
                            if (f.type === 'textarea') return React.createElement('td', { key: f.key, className: 'desc-cell', title: v || '' }, (v && v.trim()) || '\u2014');
                            return React.createElement('td', { key: f.key, style: { fontWeight: 600 } }, v || '\u2014');
                          }),
                          React.createElement('td', null,
                            React.createElement('button', { className: 'bdg ' + (st === 'inactive' ? 'bdg-b' : 'bdg-g'), title: 'Click to toggle', onClick: function () { handleToggleStatus(item); } }, st.toUpperCase())
                          ),
                          React.createElement('td', { className: 'actions-cell' },
                            React.createElement('div', { className: 'row-actions' },
                              React.createElement('button', { className: 'row-action-btn row-action-view', title: 'View', 'aria-label': 'View', type: 'button', onClick: function (e) { e.stopPropagation(); setViewItem(item); } }, React.createElement(EyeIcon, null)),
                              React.createElement('button', { className: 'row-action-btn row-action-edit', title: 'Edit', 'aria-label': 'Edit', type: 'button', onClick: function (e) { e.stopPropagation(); handleEdit(item); } }, React.createElement(PencilIcon, null)),
                              React.createElement('button', { className: 'row-action-btn row-action-delete', title: 'Delete', 'aria-label': 'Delete', type: 'button', onClick: function (e) { e.stopPropagation(); requestDelete(item); } }, React.createElement(TrashIcon, null))
                            )
                          )
                        );
                      })
                )
              )
            ),
            React.createElement(Pagination, { currentPage: currentPage, totalPages: totalPages, onPageChange: setCurrentPage, totalItems: filteredItems.length })
          )
    )
  );
};

// ---------------------------------------------------------------------------
// Main Section form (singleton record — no list/modal, just inline editing)
// ---------------------------------------------------------------------------
var MAIN_NAME_LIMIT = 80;
var MAIN_HIGHLIGHT_LIMIT = 80;
var MAIN_DESC_LIMIT = 600;
var MAIN_BTN_LIMIT = 40;

var MainSectionForm = function () {
  var b1 = useState(true); var loading = b1[0]; var setLoading = b1[1];
  var b2 = useState(false); var submitLoading = b2[0]; var setSubmitLoading = b2[1];
  var b3 = useState({ text: '', type: '' }); var msg = b3[0]; var setMsg = b3[1];
  var b4 = useState(''); var eyebrowText = b4[0]; var setEyebrowText = b4[1];
  var b5 = useState(''); var mainTitle = b5[0]; var setMainTitle = b5[1];
  var b6 = useState(''); var highlightedTitle = b6[0]; var setHighlightedTitle = b6[1];
  var b7 = useState(''); var description = b7[0]; var setDescription = b7[1];
  var b8 = useState(''); var buttonText = b8[0]; var setButtonText = b8[1];
  var b9 = useState(''); var buttonLink = b9[0]; var setButtonLink = b9[1];
  var b10 = useState('active'); var status = b10[0]; var setStatus = b10[1];
  var b11 = useState(null); var imageFile = b11[0]; var setImageFile = b11[1];
  var b12 = useState(''); var imagePreview = b12[0]; var setImagePreview = b12[1];
  var b13 = useState(false); var imageRemoved = b13[0]; var setImageRemoved = b13[1];
  var b14 = useState(false); var showSizeWarn = b14[0]; var setShowSizeWarn = b14[1];
  var b15 = useState(false); var showTypeWarn = b15[0]; var setShowTypeWarn = b15[1];
  var b16 = useState(false); var loaded = b16[0]; var setLoaded = b16[1];
  var b17 = useState(''); var recordId = b17[0]; var setRecordId = b17[1];
  var b18 = useState(false); var deleteLoading = b18[0]; var setDeleteLoading = b18[1];
  var b19 = useState(false); var showDeleteConfirm = b19[0]; var setShowDeleteConfirm = b19[1];
  var b20 = useState(false); var deleteSuccessMain = b20[0]; var setDeleteSuccessMain = b20[1];

  var fileInputRef = useRef();

  var showMsg = function (text, type) {
    setMsg({ text: text, type: type });
    setTimeout(function () { setMsg({ text: '', type: '' }); }, 5000);
  };

  var resetFileInput = function () { try { if (fileInputRef.current) fileInputRef.current.value = ''; } catch (e) { /* noop */ } };
  var clearFields = function () {
    setRecordId('');
    setEyebrowText('');
    setMainTitle('');
    setHighlightedTitle('');
    setDescription('');
    setButtonText('');
    setButtonLink('');
    setStatus('active');
    setImageFile(null);
    setImagePreview('');
    setImageRemoved(false);
    resetFileInput();
  };
  var clearImage = function () { setImagePreview(''); setImageFile(null); setImageRemoved(true); resetFileInput(); };

  var validateAndSetImage = function (file) {
    if (!file) return;
    if (file.type !== 'image/jpeg' && file.type !== 'image/png') { resetFileInput(); setShowTypeWarn(true); return; }
    if (file.size > MAX_IMAGE_BYTES) { resetFileInput(); setShowSizeWarn(true); return; }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setImageRemoved(false);
  };

  var handleImageChange = function (e) { validateAndSetImage(e.target.files && e.target.files[0]); };
  var handleImageDrop = function (e) {
    e.preventDefault();
    e.currentTarget.classList.remove('drag-over');
    validateAndSetImage(e.dataTransfer.files && e.dataTransfer.files[0]);
  };

  var fetchData = function () {
    setLoading(true);
    getAboutMain()
      .then(function (res) {
        var rec = extractRecord(res, 'main') || {};
        if (!rec || rec.isDeleted === true) { clearFields(); setLoaded(true); return; }
        setRecordId(rec._id || rec.id || '');
        setEyebrowText(rec.eyebrowText || '');
        setMainTitle(rec.mainTitle || '');
        setHighlightedTitle(rec.highlightedTitle || '');
        setDescription(rec.description || '');
        setButtonText(rec.buttonText || '');
        setButtonLink(rec.buttonLink || '');
        setStatus(rec.status || 'active');
        setImagePreview(extractImageSrc(rec, 'mainImage'));
        setLoaded(true);
      })
      .catch(function (err) {
        if (err && err.response && (err.response.status === 404 || err.response.status === 403)) { clearFields(); setLoaded(true); return; }
        showMsg((err.response && err.response.data && (err.response.data.msg || err.response.data.message)) || 'Failed to load About Main section', 'e');
      })
      .finally(function () { setLoading(false); });
  };

  useEffect(function () { fetchData(); }, []); // eslint-disable-line


  var getCurrentAboutRecord = async function () {
    try {
      var res = await getAboutMain();
      var rec = extractRecord(res, 'main');
      if (rec && (rec._id || rec.id)) return rec;
      return null;
    } catch (err) {
      if (err && err.response && err.response.status === 404) return null;
      throw err;
    }
  };

  var getBackendErrorMessage = function (err) {
    var data = err && err.response && err.response.data;
    if (err && err.response && err.response.status === 400) {
      console.error('About Main Section 400 response:', data);
    }
    return (data && (data.message || data.msg || data.error)) || 'An error occurred';
  };

  var handleSubmit = async function () {
    setSubmitLoading(true);
    try {
      var currentId = recordId;
      if (!currentId) {
        var existing = await getCurrentAboutRecord();
        currentId = existing && (existing._id || existing.id);
        if (currentId) setRecordId(currentId);
      }

      var requiredFields = [
        { label: 'Eyebrow Text', value: eyebrowText },
        { label: 'Main Title', value: mainTitle },
        { label: 'Highlighted Title', value: highlightedTitle },
        { label: 'Description', value: description },
        { label: 'Button Text', value: buttonText },
        { label: 'Button Link', value: buttonLink }
      ];
      for (var i = 0; i < requiredFields.length; i++) {
        if (!String(requiredFields[i].value || '').trim()) {
          showMsg(requiredFields[i].label + ' is required', 'e');
          setSubmitLoading(false);
          return;
        }
      }
      if (status !== 'active' && status !== 'inactive') {
        showMsg('Status must be active or inactive', 'e');
        setSubmitLoading(false);
        return;
      }
      if (!currentId && !imageFile && !imagePreview) {
        showMsg('Main Image is required', 'e');
        setSubmitLoading(false);
        return;
      }
      if (imageFile && imageFile.size > MAX_IMAGE_BYTES) {
        setImageFile(null); setImagePreview(''); resetFileInput(); setShowSizeWarn(true); setSubmitLoading(false); return;
      }

      var fd = new FormData();
      fd.append('eyebrowText', eyebrowText.trim());
      fd.append('mainTitle', mainTitle.trim());
      fd.append('highlightedTitle', highlightedTitle.trim());
      fd.append('description', description.trim());
      fd.append('buttonText', buttonText.trim());
      fd.append('buttonLink', buttonLink.trim());
      fd.append('status', status);
      if (imageRemoved) fd.append('removeImage', 'true');
      else if (imageFile) fd.append('mainImage', imageFile);

      if (process.env.NODE_ENV !== 'production') {
        console.log('About Main Section request fields:', Array.from(fd.keys()));
      }

      if (currentId) {
        await updateAboutMain(currentId, fd);
        showMsg('About Main Section Updated successfully', 's');
      } else {
        try {
          await createAboutMain(fd);
          showMsg('About Main Section Created successfully', 's');
        } catch (createErr) {
          var statusCode = createErr && createErr.response && createErr.response.status;
          if (statusCode !== 400) throw createErr;
          console.error('About Main Section create failed with 400:', createErr.response && createErr.response.data);
          var latest = await getCurrentAboutRecord();
          var latestId = latest && (latest._id || latest.id);
          if (!latestId) throw createErr;
          setRecordId(latestId);
          await updateAboutMain(latestId, fd);
          showMsg('About Main Section Updated successfully', 's');
        }
      }
      setImageFile(null); setImageRemoved(false);
      fetchData();
    } catch (err) {
      console.error('Submit failed:', err);
      showMsg(getBackendErrorMessage(err), 'e');
    } finally {
      setSubmitLoading(false);
    }
  };
  var handleDelete = async function () {
    if (!recordId) { showMsg('No About Main section found to delete', 'e'); return; }
    setShowDeleteConfirm(false);
    setDeleteLoading(true);
    try {
      await deleteAboutMain(recordId);
      setDeleteSuccessMain(true);
    } catch (err) {
      console.error('Delete failed:', err);
      showMsg((err.response && err.response.data && (err.response.data.message || err.response.data.msg)) || 'Delete failed', 'e');
    } finally {
      setDeleteLoading(false);
    }
  };

  var handleDeleteSuccessMainClose = function () {
    setDeleteSuccessMain(false);
    clearFields();
    fetchData();
  };

  if (loading && !loaded) return React.createElement('div', { className: 'wid' }, React.createElement('div', { className: 'loading-spin' }));

  return React.createElement(React.Fragment, null,
    showSizeWarn && React.createElement(ImageWarningModal, { kind: 'size', onClose: function () { setShowSizeWarn(false); } }),
    showTypeWarn && React.createElement(ImageWarningModal, { kind: 'type', onClose: function () { setShowTypeWarn(false); } }),
    showDeleteConfirm && React.createElement(DeleteConfirmModal, {
      itemName: 'About Main Section',
      message: 'Are you sure you want to delete the About Main Section?',
      isDeleting: deleteLoading,
      onConfirm: handleDelete,
      onCancel: function () { setShowDeleteConfirm(false); }
    }),
    deleteSuccessMain && React.createElement(DeleteSuccessModal, {
      isOpen: true,
      message: 'Deleted Successfully',
      subtitle: 'The About Main section has been deleted successfully.',
      onClose: handleDeleteSuccessMainClose
    }),

    msg.text && React.createElement('div', { className: 'mbx ' + (msg.type === 'e' ? 'mbx-e' : 'mbx-s') }, msg.text),

    React.createElement('div', { className: 'wid' },
      React.createElement('div', { className: 'wh' }, React.createElement('span', { className: 'wt' }, 'About Main Section')),

      React.createElement('div', { className: 'about-main-grid' },

        React.createElement('div', { className: 'fl' },
          React.createElement('label', null, 'Eyebrow Text', React.createElement('span', { className: 'char-count' }, eyebrowText.length + '/60')),
          React.createElement('input', { type: 'text', placeholder: 'e.g. Our Story', value: eyebrowText, maxLength: 60, onChange: function (e) { setEyebrowText(e.target.value); } })
        ),

        React.createElement('div', { className: 'fl' },
          React.createElement('label', null, 'Main Title *', React.createElement('span', { className: 'char-count' + (mainTitle.length >= MAIN_NAME_LIMIT ? ' char-warn' : '') }, mainTitle.length + '/' + MAIN_NAME_LIMIT)),
          React.createElement('input', { type: 'text', placeholder: 'e.g. Crafting Timeless', value: mainTitle, maxLength: MAIN_NAME_LIMIT, onChange: function (e) { setMainTitle(e.target.value); } })
        ),

        React.createElement('div', { className: 'fl' },
          React.createElement('label', null, 'Highlighted Title', React.createElement('span', { className: 'char-count' + (highlightedTitle.length >= MAIN_HIGHLIGHT_LIMIT ? ' char-warn' : '') }, highlightedTitle.length + '/' + MAIN_HIGHLIGHT_LIMIT)),
          React.createElement('input', { type: 'text', placeholder: 'e.g. Elegance', value: highlightedTitle, maxLength: MAIN_HIGHLIGHT_LIMIT, onChange: function (e) { setHighlightedTitle(e.target.value); } })
        ),

        React.createElement('div', { className: 'fl' },
          React.createElement('label', null, 'Status'),
          React.createElement(CustomSelect, { value: status, onChange: setStatus, options: STATUS_OPTIONS })
        ),

        React.createElement('div', { className: 'fl' },
          React.createElement('label', null, 'Button Text', React.createElement('span', { className: 'char-count' }, buttonText.length + '/' + MAIN_BTN_LIMIT)),
          React.createElement('input', { type: 'text', placeholder: 'e.g. Explore Collection', value: buttonText, maxLength: MAIN_BTN_LIMIT, onChange: function (e) { setButtonText(e.target.value); } })
        ),

        React.createElement('div', { className: 'fl' },
          React.createElement('label', null, 'Button Link'),
          React.createElement('input', { type: 'text', placeholder: 'e.g. /products', value: buttonLink, onChange: function (e) { setButtonLink(e.target.value); } })
        ),

        React.createElement('div', { className: 'fl', style: { gridColumn: '1 / -1' } },
          React.createElement('label', null, 'Description', React.createElement('span', { className: 'char-count' + (description.length >= MAIN_DESC_LIMIT ? ' char-warn' : '') }, description.length + '/' + MAIN_DESC_LIMIT)),
          React.createElement('textarea', { rows: 5, placeholder: 'Tell the brand story...', value: description, maxLength: MAIN_DESC_LIMIT, onChange: function (e) { setDescription(e.target.value); } })
        ),

        React.createElement('div', { className: 'fl', style: { gridColumn: '1 / -1' } },
          React.createElement('label', null, 'Main Image'),
          imagePreview
            ? React.createElement('div', { className: 'img-preview-wrap', onClick: function () { if (fileInputRef.current) fileInputRef.current.click(); } },
                React.createElement('input', { type: 'file', accept: 'image/jpeg,image/png', ref: fileInputRef, style: { display: 'none' }, onChange: handleImageChange }),
                React.createElement('img', { src: imagePreview, alt: 'preview', className: 'img-preview', onError: function (e) { e.target.style.display = 'none'; } }),
                React.createElement('div', { className: 'img-preview-actions' },
                  React.createElement('button', { className: 'img-btn', type: 'button', onClick: function (e) { e.stopPropagation(); if (fileInputRef.current) fileInputRef.current.click(); } }, 'Change'),
                  React.createElement('button', { className: 'img-remove', type: 'button', onClick: function (e) { e.stopPropagation(); clearImage(); } }, React.createElement(XIcon, null))
                )
              )
            : React.createElement('div', { className: 'img-upload-zone', onClick: function () { if (fileInputRef.current) fileInputRef.current.click(); }, onDragOver: function (e) { e.preventDefault(); e.currentTarget.classList.add('drag-over'); }, onDragLeave: function (e) { e.currentTarget.classList.remove('drag-over'); }, onDrop: handleImageDrop },
                React.createElement('input', { type: 'file', accept: 'image/jpeg,image/png', ref: fileInputRef, style: { display: 'none' }, onChange: handleImageChange }),
                React.createElement('span', { style: { color: '#3E7C8C' } }, React.createElement(UploadIcon, null)),
                React.createElement('span', { className: 'img-upload-hint' }, React.createElement('strong', null, 'Click to upload'), ' or drag & drop', React.createElement('br', null), 'PNG, JPG \u2014 max 3.5\u00A0MB')
              )
        ),

        React.createElement('div', { className: 'about-main-actions' },
          recordId && React.createElement('button', { className: 'btn btn-danger', type: 'button', disabled: deleteLoading || submitLoading, style: { opacity: deleteLoading || submitLoading ? 0.7 : 1 }, onClick: function () { setShowDeleteConfirm(true); } }, deleteLoading ? 'Deleting...' : 'Delete'),
          React.createElement('button', { className: 'btn btn-p', type: 'button', disabled: submitLoading || deleteLoading, style: { opacity: submitLoading || deleteLoading ? 0.7 : 1 }, onClick: handleSubmit }, submitLoading ? 'Saving...' : (recordId ? 'Save Changes' : 'Create'))
        )
      )
    )
  );
};

// ---------------------------------------------------------------------------
// Top-level About Us admin page — tab switcher
// ---------------------------------------------------------------------------
var TABS = [
  { key: 'main', label: 'Main Section' },
  { key: 'statistics', label: 'Statistics' },
  { key: 'features', label: 'Crafted With Purpose' },
  { key: 'team', label: 'Our Team' },
];

var AboutUsAdmin = function () {
  var t = useState('main'); var activeTab = t[0]; var setActiveTab = t[1];

  return React.createElement(AdminLayout, null,
    React.createElement('div', { className: 'about-admin-page' },
      React.createElement('div', { className: 'about-bg-shapes', 'aria-hidden': 'true' },
        React.createElement('div', { className: 'about-bg-shape about-bg-shape-1' }),
        React.createElement('div', { className: 'about-bg-shape about-bg-shape-2' }),
        React.createElement('div', { className: 'about-bg-shape about-bg-shape-3' }),
        React.createElement('div', { className: 'about-bg-shape about-bg-shape-ring-1' }),
        React.createElement('div', { className: 'about-bg-shape about-bg-shape-ring-2' })
      ),

      React.createElement('div', { className: 'page-header-row' },
        React.createElement('div', { className: 'page-header-main' },
          React.createElement('div', { className: 'page-header-title-row' },
            React.createElement('div', { className: 'page-header-icon' },
              React.createElement('svg', { viewBox: '0 0 24 24', width: 18, height: 18, fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
                React.createElement('circle', { cx: 12, cy: 12, r: 10 }),
                React.createElement('line', { x1: 12, y1: 16, x2: 12, y2: 12 }),
                React.createElement('line', { x1: 12, y1: 8, x2: 12.01, y2: 8 })
              )
            ),
            React.createElement('div', { className: 'ptitle' }, 'About Us')
          ),
          React.createElement('div', { className: 'header-subtitle' }, 'Manage the content shown on the storefront About page')
        )
      ),

      React.createElement('div', { className: 'about-tabs' },
        TABS.map(function (tab) {
          return React.createElement('button', {
            key: tab.key,
            type: 'button',
            className: 'about-tab-btn' + (activeTab === tab.key ? ' active' : ''),
            onClick: function () { setActiveTab(tab.key); }
          }, tab.label);
        })
      ),

      activeTab === 'main' && React.createElement(MainSectionForm, null),

      activeTab === 'statistics' && React.createElement(ManagedTable, {
        singular: 'Statistic',
        plural: 'Statistics',
        addLabel: 'Add Statistic',
        hasImage: false,
        listKey: 'statistics',
        api: { getAll: getAllStatistics, create: createStatistic, update: updateStatistic, remove: deleteStatistic },
        fields: [
          { key: 'value', label: 'Value', type: 'text', required: true, maxLength: 20, showInTable: true, placeholder: 'e.g. 92.5%, 100%, 25+, 10K+' },
          { key: 'title', label: 'Title', type: 'text', required: true, maxLength: 60, showInTable: true, placeholder: 'e.g. Client Satisfaction' },
          { key: 'order', label: 'Display Order', type: 'number', required: false, showInTable: true, placeholder: 'e.g. 1 (lower shows first)' }
        ]
      }),

      activeTab === 'features' && React.createElement(ManagedTable, {
        singular: 'Feature',
        plural: 'Crafted With Purpose Items',
        addLabel: 'Add Feature',
        hasImage: false,
        listKey: 'features',
        api: { getAll: getAllFeatures, create: createFeature, update: updateFeature, remove: deleteFeature },
        fields: [
          { key: 'icon', label: 'Icon', type: 'text', required: true, maxLength: 40, showInTable: true, placeholder: 'e.g. diamond' },
          { key: 'title', label: 'Title', type: 'text', required: true, maxLength: 60, showInTable: true, placeholder: 'e.g. Ethical Sourcing' },
          { key: 'description', label: 'Description', type: 'textarea', required: false, maxLength: 160, showInTable: true, placeholder: 'Short description of this value' },
          { key: 'order', label: 'Display Order', type: 'number', required: false, showInTable: true, placeholder: 'e.g. 1 (lower shows first)' }
        ]
      }),

      activeTab === 'team' && React.createElement(ManagedTable, {
        singular: 'Team Member',
        plural: 'Team Members',
        addLabel: 'Add Team Member',
        hasImage: true,
        imageLabel: 'Photo',
        imageKey: 'image',
        imageHint: 'PNG, JPG \u2014 max 3.5\u00A0MB',
        listKey: 'team',
        api: { getAll: getAllTeamMembers, create: createTeamMember, update: updateTeamMember, remove: deleteTeamMember },
        fields: [
          { key: 'name', label: 'Name', type: 'text', required: true, maxLength: 60, showInTable: true, placeholder: 'e.g. Priya Sharma' },
          { key: 'designation', label: 'Designation', type: 'text', required: true, maxLength: 60, showInTable: true, placeholder: 'e.g. Creative Director' },
          { key: 'description', label: 'Description', type: 'textarea', required: false, maxLength: 160, showInTable: true, placeholder: 'Short bio here' },
          { key: 'order', label: 'Display Order', type: 'number', required: false, showInTable: true, placeholder: 'e.g. 1 (lower shows first)' }
        ]
      })
    )
  );
};

export default AboutUsAdmin;







